// SPV Pool v1.0 PR 5 — typed transport for the four service-provider
// counterparty entities plus the SpvPoolServiceProvider override join.
// Mutation hooks live in @/hooks/capitalMarkets/useServiceProviders.

import { api } from "./client";
import type { PagedResult } from "@/types/api";
import type {
  AnyServiceProviderDto,
  BackupServicerCreate,
  BackupServicerDto,
  IndentureTrusteeCreate,
  IndentureTrusteeDto,
  PlacementAgentCreate,
  PlacementAgentDto,
  PoolServedDto,
  ServiceProviderListQuery,
  ServiceProviderType,
  ServiceProviderUpdate,
  TrustCompanyCreate,
  TrustCompanyDto,
} from "@/types/serviceProviders";

/** REST path segment per provider type. */
export const PROVIDER_ROUTE: Record<ServiceProviderType, string> = {
  IndentureTrustee: "indenture-trustees",
  TrustCompany: "trust-companies",
  PlacementAgent: "placement-agents",
  BackupServicer: "backup-servicers",
};

export const indentureTrusteesApi = {
  list: (query?: ServiceProviderListQuery) =>
    api.get<PagedResult<IndentureTrusteeDto>>("/indenture-trustees", query),
  get: (id: string) => api.get<IndentureTrusteeDto>(`/indenture-trustees/${id}`),
  poolsServed: (id: string) =>
    api.get<PoolServedDto[]>(`/indenture-trustees/${id}/pools-served`),
  create: (body: IndentureTrusteeCreate) =>
    api.post<IndentureTrusteeDto>("/indenture-trustees", body),
  update: (id: string, body: ServiceProviderUpdate) =>
    api.put<IndentureTrusteeDto>(`/indenture-trustees/${id}`, body),
  delete: (id: string) => api.delete<void>(`/indenture-trustees/${id}`),
};

export const trustCompaniesApi = {
  list: (query?: ServiceProviderListQuery) =>
    api.get<PagedResult<TrustCompanyDto>>("/trust-companies", query),
  get: (id: string) => api.get<TrustCompanyDto>(`/trust-companies/${id}`),
  poolsServed: (id: string) =>
    api.get<PoolServedDto[]>(`/trust-companies/${id}/pools-served`),
  create: (body: TrustCompanyCreate) =>
    api.post<TrustCompanyDto>("/trust-companies", body),
  update: (id: string, body: ServiceProviderUpdate) =>
    api.put<TrustCompanyDto>(`/trust-companies/${id}`, body),
  delete: (id: string) => api.delete<void>(`/trust-companies/${id}`),
};

export const placementAgentsApi = {
  list: (query?: ServiceProviderListQuery) =>
    api.get<PagedResult<PlacementAgentDto>>("/placement-agents", query),
  get: (id: string) => api.get<PlacementAgentDto>(`/placement-agents/${id}`),
  poolsServed: (id: string) =>
    api.get<PoolServedDto[]>(`/placement-agents/${id}/pools-served`),
  create: (body: PlacementAgentCreate) =>
    api.post<PlacementAgentDto>("/placement-agents", body),
  update: (id: string, body: ServiceProviderUpdate) =>
    api.put<PlacementAgentDto>(`/placement-agents/${id}`, body),
  delete: (id: string) => api.delete<void>(`/placement-agents/${id}`),
};

export const backupServicersApi = {
  list: (query?: ServiceProviderListQuery) =>
    api.get<PagedResult<BackupServicerDto>>("/backup-servicers", query),
  get: (id: string) => api.get<BackupServicerDto>(`/backup-servicers/${id}`),
  poolsServed: (id: string) =>
    api.get<PoolServedDto[]>(`/backup-servicers/${id}/pools-served`),
  create: (body: BackupServicerCreate) =>
    api.post<BackupServicerDto>("/backup-servicers", body),
  update: (id: string, body: ServiceProviderUpdate) =>
    api.put<BackupServicerDto>(`/backup-servicers/${id}`, body),
  delete: (id: string) => api.delete<void>(`/backup-servicers/${id}`),
};

/** Resolves the right transport object for a given provider type. */
export function providerApiFor(type: ServiceProviderType) {
  switch (type) {
    case "IndentureTrustee":
      return indentureTrusteesApi;
    case "TrustCompany":
      return trustCompaniesApi;
    case "PlacementAgent":
      return placementAgentsApi;
    case "BackupServicer":
      return backupServicersApi;
  }
}

export type ProviderListResult = PagedResult<AnyServiceProviderDto>;
